/**
 * The cash book and bank reconciliation.
 *
 * ═══════════════════════════════════════════════════════════════════
 * A SUGGESTION IS A SUGGESTION.
 *
 * The matcher proposes candidates for each unreconciled statement line, with a
 * confidence and the reason it thinks they belong together. It never reconciles
 * anything by itself. Where nothing matches, that is reported plainly as
 * needing a person — which is the genuinely useful signal, and the one an
 * over-eager auto-matcher destroys.
 * ═══════════════════════════════════════════════════════════════════
 *
 * The matcher works one bank account at a time, because a match only means
 * anything inside the account the money moved through. That is why the third
 * tab asks which account before it asks anything else.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { lower, useTerms } from '../lib/tenant';
import { date, money, statusLabel } from '../lib/format';
import { AsyncPanel, Badge, Card, DataTable, EmptyState, Money, PageHeader, Select, Stat, Tabs } from '../components/ui';

/**
 * An integer count of minor units, as it arrives on the wire.
 *
 * Which JSON type a BIGINT lands in depends on the driver — the in-process
 * database returns a number, node-postgres a string — so every one of these
 * goes through Number() at the point it is used, and no arithmetic happens
 * anywhere else.
 */
type Minor = number | string;

interface BankAccount {
  id: string;
  code: string;
  name: string;
  bank_name: string;
  account_type: string;
  sort_code: string;
  account_number_masked: string;
  ledger_balance_minor: Minor;
  statement_balance_minor: Minor;
  last_statement_date: string | null;
  gl_account_code: string;
  unreconciled_count: Minor;
}

interface BankTransaction {
  id: string;
  bank_account_id: string;
  transaction_date: string;
  description: string;
  reference: string | null;
  transaction_type: string;
  amount_minor: Minor;
  is_duplicate_suspect: boolean;
  bank_account_name: string;
}

/** Exactly the shape /receivables/bank/suggest-matches returns. */
interface MatchCandidate {
  id: string;
  table: string;
  reference: string;
  description: string;
  amountMinor: number;
  date: string;
  confidence: number;
  reason: string;
}

interface MatchSuggestion {
  bankTransaction: {
    id: string;
    date: string;
    description: string;
    reference: string | null;
    amountMinor: number;
  };
  candidates: MatchCandidate[];
  requiresManualAttention: boolean;
}

interface MatchResponse {
  suggestions: MatchSuggestion[];
  summary: { total: number; withSuggestions: number; needingAttention: number };
}

/** A UK sort code is read in pairs — 20-30-45 — never as six unbroken digits. */
function sortCode(value: string): string {
  return /^\d{6}$/.test(value) ? `${value.slice(0, 2)}-${value.slice(2, 4)}-${value.slice(4)}` : value;
}

export function CashPage() {
  const { can } = useSession();
  const t = useTerms();

  /*
   * The view lives in the address bar so that a headline figure can link
   * straight to the lines behind it, and so that "the unreconciled items on the
   * main current account" survives a reload and can be sent to somebody else.
   */
  const [params, setParams] = useSearchParams();

  // Suggesting matches needs the reconcile permission, so a user who may only
  // look at the cash book is never shown the tab — a tab that could only
  // answer with a refusal is worse than no tab.
  const canReconcile = can('cash.reconcile');
  const requested = params.get('tab');
  const tab = requested === 'unreconciled' || (requested === 'reconcile' && canReconcile) ? requested : 'accounts';
  const focusAccountId = params.get('account');

  const setView = (nextTab: string, accountId: string | null = focusAccountId) => {
    const next: Record<string, string> = {};
    if (nextTab !== 'accounts') next.tab = nextTab;
    if (accountId) next.account = accountId;
    setParams(next, { replace: true });
  };

  const accounts = useAsync(() => api.get<{ accounts: BankAccount[] }>('/receivables/bank/accounts'), []);

  const transactions = useAsync(
    () =>
      api.get<{ transactions: BankTransaction[] }>(
        `/receivables/bank/transactions?status=unreconciled${
          focusAccountId ? `&bankAccountId=${encodeURIComponent(focusAccountId)}` : ''
        }`,
      ),
    [focusAccountId],
  );

  // The badge counts every unreconciled line in the cash book, not the filtered
  // list, so choosing one account does not appear to make items disappear.
  const totalUnreconciled = accounts.data
    ? accounts.data.accounts.reduce((sum, a) => sum + Number(a.unreconciled_count), 0)
    : undefined;

  const focused = accounts.data?.accounts.find((a) => a.id === focusAccountId) ?? null;

  const tabs: Array<{ key: string; label: string; badge?: number }> = [
    { key: 'accounts', label: 'Accounts' },
    { key: 'unreconciled', label: 'Unreconciled', badge: totalUnreconciled },
  ];
  if (canReconcile) tabs.push({ key: 'reconcile', label: 'Suggested matches' });

  return (
    <>
      <PageHeader title="Cash book" subtitle="Bank balances, unreconciled items, and the reconciliation." />

      <Tabs active={tab} onChange={(key) => setView(key)} tabs={tabs} />

      {tab === 'accounts' ? (
        <AsyncPanel state={accounts}>
          {(data) => {
            const ledger = data.accounts.reduce((sum, a) => sum + Number(a.ledger_balance_minor), 0);
            const statement = data.accounts.reduce((sum, a) => sum + Number(a.statement_balance_minor), 0);
            const unreconciled = data.accounts.reduce((sum, a) => sum + Number(a.unreconciled_count), 0);
            const difference = statement - ledger;
            const statementDates = data.accounts
              .map((a) => a.last_statement_date)
              .filter((value): value is string => Boolean(value))
              .sort();
            const latestStatement = statementDates[statementDates.length - 1];

            return (
              <div className="stack">
                <div className="grid grid--3">
                  <Stat
                    label="Cash at bank"
                    value={money(ledger)}
                    detail={`Per the ledger · ${data.accounts.length} account${data.accounts.length === 1 ? '' : 's'}`}
                  />
                  <Stat
                    label="Per the statement"
                    value={money(statement)}
                    detail={latestStatement ? `As at ${date(latestStatement)}` : undefined}
                  />
                  <Stat
                    label="Unreconciled items"
                    value={String(unreconciled)}
                    tone={unreconciled > 0 ? 'negative' : undefined}
                    detail={
                      difference === 0
                        ? 'The ledger and the statement agree'
                        : `${money(Math.abs(difference))} between the ledger and the statement`
                    }
                    to={unreconciled > 0 ? '/cash?tab=unreconciled' : undefined}
                    drillLabel="See the lines"
                  />
                </div>

                <Card
                  title="Bank accounts"
                  subtitle="Choose an account to see the statement lines behind its unreconciled count."
                  flush
                >
                  <DataTable
                    caption="Bank accounts"
                    rows={data.accounts}
                    rowKey={(a) => a.id}
                    onRowClick={(a) => setView('unreconciled', a.id)}
                    columns={[
                      { key: 'code', header: 'Code', width: '6rem', render: (a) => <span className="mono">{a.code}</span> },
                      {
                        key: 'name',
                        header: 'Account',
                        render: (a) => (
                          <>
                            <div>{a.name}</div>
                            <div className="smaller muted">
                              {a.bank_name} · {sortCode(a.sort_code)} {a.account_number_masked}
                            </div>
                          </>
                        ),
                      },
                      { key: 'type', header: 'Type', render: (a) => statusLabel(a.account_type) },
                      { key: 'gl', header: 'Nominal', render: (a) => <span className="mono smaller">{a.gl_account_code}</span> },
                      {
                        key: 'ledger',
                        header: 'Per the ledger',
                        numeric: true,
                        render: (a) => <Money minor={Number(a.ledger_balance_minor)} />,
                      },
                      {
                        key: 'statement',
                        header: 'Per the statement',
                        numeric: true,
                        render: (a) => <Money minor={Number(a.statement_balance_minor)} />,
                      },
                      {
                        key: 'diff',
                        header: 'Difference',
                        numeric: true,
                        render: (a) => (
                          <Money minor={Number(a.statement_balance_minor) - Number(a.ledger_balance_minor)} blankIfZero />
                        ),
                      },
                      {
                        key: 'unrec',
                        header: 'Unreconciled',
                        numeric: true,
                        render: (a) =>
                          Number(a.unreconciled_count) > 0 ? (
                            <Badge tone="warn">{Number(a.unreconciled_count)}</Badge>
                          ) : (
                            <Badge tone="ok">None</Badge>
                          ),
                      },
                    ]}
                  />
                </Card>
              </div>
            );
          }}
        </AsyncPanel>
      ) : tab === 'unreconciled' ? (
        <Card
          title="Unreconciled statement lines"
          subtitle={
            focusAccountId ? (focused ? `${focused.name} · ${focused.code}` : 'One bank account') : 'Every bank account'
          }
          actions={
            focusAccountId ? (
              <button type="button" className="btn btn--sm" onClick={() => setView('unreconciled', null)}>
                Show every account
              </button>
            ) : null
          }
          flush
        >
          <AsyncPanel state={transactions}>
            {(data) => (
              <DataTable
                caption="Bank statement lines not yet reconciled"
                rows={data.transactions}
                rowKey={(tx) => tx.id}
                emptyMessage={
                  focused
                    ? `Everything on ${focused.name} has been reconciled.`
                    : 'Everything on the statement has been reconciled.'
                }
                initialSort={{ key: 'date', direction: 'desc' }}
                /*
                 * Deliberately no search box: the footer totals every row in the
                 * table, and a filtered list under an unfiltered total is the
                 * kind of thing a reconciliation working paper cannot have.
                 */
                footer={
                  <tr className="is-total">
                    <td colSpan={4}>Total unreconciled</td>
                    <td className="num">
                      <Money minor={data.transactions.reduce((sum, tx) => sum + Number(tx.amount_minor), 0)} bold />
                    </td>
                  </tr>
                }
                columns={[
                  {
                    key: 'date',
                    header: 'Date',
                    width: '7rem',
                    render: (tx) => date(tx.transaction_date),
                    sortValue: (tx) => tx.transaction_date,
                  },
                  {
                    key: 'desc',
                    header: 'Narrative',
                    render: (tx) => (
                      <>
                        <div className="mono smaller">{tx.description}</div>
                        {/* The account name only earns its line when more than one account is on screen. */}
                        {focusAccountId ? null : <div className="smaller muted">{tx.bank_account_name}</div>}
                        {tx.is_duplicate_suspect ? <Badge tone="warn">Possible duplicate</Badge> : null}
                      </>
                    ),
                  },
                  { key: 'ref', header: 'Reference', render: (tx) => <span className="mono smaller">{tx.reference ?? '—'}</span> },
                  { key: 'type', header: 'Type', render: (tx) => statusLabel(tx.transaction_type) },
                  {
                    key: 'amount',
                    header: 'Amount',
                    numeric: true,
                    render: (tx) => <Money minor={Number(tx.amount_minor)} />,
                    sortValue: (tx) => Number(tx.amount_minor),
                  },
                ]}
              />
            )}
          </AsyncPanel>
        </Card>
      ) : (
        <AsyncPanel state={accounts}>
          {(data) =>
            data.accounts.length === 0 ? (
              <EmptyState title="There are no bank accounts to reconcile">
                No bank account has been set up for this {lower(t.entity)} yet, so there is nothing for the matcher to
                look at.
              </EmptyState>
            ) : (
              <ReconcilePanel accounts={data.accounts} />
            )
          }
        </AsyncPanel>
      )}
    </>
  );
}

/**
 * The suggested matches for one bank account.
 *
 * Mounted only when the tab is open, and only once the accounts are known, so
 * the account it asks about is always a real one. The endpoint requires a bank
 * account and refuses without it, which is the correct behaviour — a matcher
 * that quietly ran across every account would offer matches between two
 * different bank accounts and call them candidates.
 */
function ReconcilePanel({ accounts }: { accounts: BankAccount[] }) {
  const t = useTerms();

  // Open on the account that actually has work to do, so the tab shows the
  // reconciliation rather than an empty account and a menu.
  const [accountId, setAccountId] = useState(
    () => accounts.find((a) => Number(a.unreconciled_count) > 0)?.id ?? accounts[0]?.id ?? '',
  );

  const suggestions = useAsync(
    () =>
      accountId
        ? api.get<MatchResponse>(`/receivables/bank/suggest-matches?bankAccountId=${encodeURIComponent(accountId)}`)
        : Promise.resolve<MatchResponse>({
            suggestions: [],
            summary: { total: 0, withSuggestions: 0, needingAttention: 0 },
          }),
    [accountId],
  );

  const chosen = accounts.find((a) => a.id === accountId) ?? null;

  // The candidate's table is the machine's word for what it found. The reviewer
  // needs the tenant's word for it — that is the first thing anybody assessing a
  // suggestion looks at.
  const candidateKind = (table: string): string =>
    table === 'payment'
      ? `${t.supplier} payment`
      : table === 'customer_receipt'
        ? `${t.customer} receipt`
        : statusLabel(table);

  return (
    <div className="stack">
      <Card title="Reconciliation" subtitle="The matcher looks at one bank account at a time.">
        <div className="row">
          <Select
            label="Bank account"
            value={accountId}
            onChange={(e) => setAccountId(e.currentTarget.value)}
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.code}) — {Number(a.unreconciled_count)} unreconciled
              </option>
            ))}
          </Select>
        </div>
      </Card>

      <AsyncPanel state={suggestions}>
        {(data) => {
          const proposed = data.suggestions.filter((s) => !s.requiresManualAttention);
          const needAPerson = data.suggestions.filter((s) => s.requiresManualAttention);

          if (data.suggestions.length === 0) {
            return (
              <EmptyState title="Nothing left to reconcile on this account" icon="✓">
                Every statement line on {chosen ? chosen.name : 'this account'} has been matched to the ledger.
              </EmptyState>
            );
          }

          return (
            <div className="stack">
              <div className="grid grid--3">
                <Stat
                  label="Lines to reconcile"
                  value={String(data.summary.total)}
                  detail={chosen ? `${chosen.name} · ${chosen.code}` : undefined}
                />
                <Stat label="With a suggested match" value={String(data.summary.withSuggestions)} />
                <Stat
                  label="Needing a person"
                  value={String(data.summary.needingAttention)}
                  tone={data.summary.needingAttention > 0 ? 'negative' : undefined}
                  detail="Nothing in the ledger resembles these"
                />
              </div>

              {proposed.map((s) => (
                <Card
                  key={s.bankTransaction.id}
                  title={s.bankTransaction.description}
                  subtitle={`${date(s.bankTransaction.date)} · ${money(s.bankTransaction.amountMinor)}${
                    s.bankTransaction.reference ? ` · ${s.bankTransaction.reference}` : ''
                  }`}
                  flush
                >
                  <DataTable
                    caption={`Candidates for ${s.bankTransaction.description}`}
                    rows={s.candidates}
                    rowKey={(c) => c.id}
                    columns={[
                      { key: 'kind', header: 'Kind', render: (c) => candidateKind(c.table) },
                      {
                        key: 'ref',
                        header: 'Reference',
                        render: (c) => (
                          <>
                            <div className="mono">{c.reference}</div>
                            {c.description ? <div className="smaller muted">{c.description}</div> : null}
                          </>
                        ),
                      },
                      { key: 'date', header: 'Date', width: '7rem', render: (c) => date(c.date) },
                      { key: 'amount', header: 'Amount', numeric: true, render: (c) => <Money minor={c.amountMinor} /> },
                      {
                        key: 'confidence',
                        header: 'Confidence',
                        render: (c) => (
                          <Badge tone={c.confidence >= 90 ? 'ok' : c.confidence >= 70 ? 'warn' : 'neutral'}>
                            {c.confidence}%
                          </Badge>
                        ),
                      },
                      { key: 'why', header: 'Why', render: (c) => <span className="smaller muted">{c.reason}</span> },
                    ]}
                  />
                </Card>
              ))}

              {needAPerson.length > 0 ? (
                <Card
                  title="No match found — these need a person"
                  subtitle="Nothing in the ledger resembles these lines. Saying so plainly is the point: a matcher eager to close the difference would attach them to the nearest thing it could find."
                  flush
                >
                  <DataTable
                    caption="Statement lines with no candidate in the ledger"
                    rows={needAPerson}
                    rowKey={(s) => s.bankTransaction.id}
                    columns={[
                      {
                        key: 'date',
                        header: 'Date',
                        width: '7rem',
                        render: (s) => date(s.bankTransaction.date),
                        sortValue: (s) => s.bankTransaction.date,
                      },
                      {
                        key: 'desc',
                        header: 'Narrative',
                        render: (s) => <span className="mono smaller">{s.bankTransaction.description}</span>,
                      },
                      {
                        key: 'ref',
                        header: 'Reference',
                        render: (s) => <span className="mono smaller">{s.bankTransaction.reference ?? '—'}</span>,
                      },
                      {
                        key: 'amount',
                        header: 'Amount',
                        numeric: true,
                        render: (s) => <Money minor={s.bankTransaction.amountMinor} />,
                        sortValue: (s) => s.bankTransaction.amountMinor,
                      },
                    ]}
                  />
                </Card>
              ) : null}

              <p className="smaller muted">
                These are suggestions with a stated reason and a confidence, proposed by the matching rules. Nothing is
                reconciled until a person accepts a match.
              </p>
            </div>
          );
        }}
      </AsyncPanel>
    </div>
  );
}
